// components/clients/ClientNotes.js
// Client Notes and Communications Component with File Uploads

import React, { useState, useEffect, useRef } from 'react';
import {
  X, Plus, Upload, File, Image as ImageIcon, Trash2, Download,
  MessageSquare, Paperclip, Calendar, User, Edit2, Save, AlertCircle,
  FileText, Video, Music, Archive, ExternalLink, Eye
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const ClientNotes = ({ 
  client, 
  currentTenant, 
  user,
  isOpen,
  onClose 
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [viewingNote, setViewingNote] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    noteType: 'general',
    isPrivate: false
  });

  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const noteTypes = [
    { value: 'general', label: 'General Note', color: 'blue', icon: MessageSquare },
    { value: 'meeting', label: 'Meeting Note', color: 'purple', icon: Calendar },
    { value: 'call', label: 'Call Log', color: 'green', icon: Calendar },
    { value: 'email', label: 'Email Summary', color: 'orange', icon: MessageSquare },
    { value: 'achievement', label: 'Achievement', color: 'yellow', icon: MessageSquare },
    { value: 'issue', label: 'Issue/Concern', color: 'red', icon: AlertCircle },
    { value: 'followup', label: 'Follow-up', color: 'indigo', icon: MessageSquare }
  ];

  useEffect(() => {
    if (!isOpen || !client?.id || !currentTenant || !user) {
      if (loading) setLoading(false);
      return;
    }
    loadNotes();
  }, [isOpen, client?.id, currentTenant, user]);

  if (!client || !currentTenant || !user) {
    console.error('ClientNotes: Missing required props', { client, currentTenant, user });
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 max-w-md">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Notes</h3>
            <p className="text-slate-600 mb-4">Missing required data. Please refresh and try again.</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const loadNotes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('client_notes')
        .select(`
          *,
          created_by_user:created_by (full_name, email),
          attachments:client_note_attachments (*)
        `)
        .eq('client_id', client.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (file, noteId) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${currentTenant.id}/${client.id}/${fileName}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('client-attachments')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('client-attachments')
      .getPublicUrl(filePath);

    const { data: attachmentData, error: attachmentError } = await supabase
      .from('client_note_attachments')
      .insert([{
        note_id: noteId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        file_type: file.type,
        file_url: publicUrl,
        uploaded_by: user.id
      }])
      .select()
      .single();

    if (attachmentError) throw attachmentError;

    return attachmentData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    console.log('Debug - Props in handleSubmit:', {
      clientId: client?.id,
      tenantId: currentTenant?.id,
      userId: user?.id
    });

    if (!client?.id) {
      setError('Client information is missing');
      return;
    }
    if (!currentTenant?.id) {
      setError('Tenant information is missing');
      return;
    }
    if (!user?.id) {
      setError('User information is missing');
      return;
    }

    if (!noteForm.title.trim() || !noteForm.content.trim()) {
      setError('Title and content are required');
      return;
    }

    setIsSubmitting(true);

    try {
      if (editingNote) {
        const { data, error: updateError } = await supabase
          .from('client_notes')
          .update({
            title: noteForm.title.trim(),
            content: noteForm.content.trim(),
            note_type: noteForm.noteType,
            is_private: noteForm.isPrivate,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingNote.id)
          .select(`
            *,
            created_by_user:created_by (full_name, email),
            attachments:client_note_attachments (*)
          `)
          .single();

        if (updateError) throw updateError;

        setNotes(prev => prev.map(note => 
          note.id === editingNote.id ? data : note
        ));

        setEditingNote(null);
      } else {
        const { data: noteData, error: noteError } = await supabase
          .from('client_notes')
          .insert([{
            tenant_id: currentTenant.id,
            client_id: client.id,
            title: noteForm.title.trim(),
            content: noteForm.content.trim(),
            note_type: noteForm.noteType,
            is_private: noteForm.isPrivate,
            created_by: user.id
          }])
          .select(`
            *,
            created_by_user:created_by (full_name, email)
          `)
          .single();

        if (noteError) throw noteError;

        if (selectedFiles.length > 0) {
          const uploadPromises = selectedFiles.map(file => uploadFile(file, noteData.id));
          const attachments = await Promise.all(uploadPromises);
          noteData.attachments = attachments;
        }

        setNotes(prev => [noteData, ...prev]);
        setSelectedFiles([]);
      }

      setNoteForm({
        title: '',
        content: '',
        noteType: 'general',
        isPrivate: false
      });

    } catch (err) {
      console.error('Error saving note:', err);
      setError(err.message || 'Failed to save note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewNote = (note) => {
    setViewingNote(note);
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const { data, error } = await supabase.storage
        .from('client-attachments')
        .download(attachment.file_path);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return ImageIcon;
    if (fileType?.startsWith('video/')) return Video;
    if (fileType?.startsWith('audio/')) return Music;
    if (fileType?.includes('pdf')) return FileText;
    if (fileType?.includes('zip') || fileType?.includes('rar')) return Archive;
    return File;
  };

  if (!isOpen) return null;

  // View Note Modal
  if (viewingNote) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <h3 className="text-xl font-semibold text-slate-900">
              {viewingNote.title}
            </h3>
            <button onClick={() => setViewingNote(null)} className="text-slate-400 hover:text-slate-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Note Metadata */}
            <div className="flex items-center space-x-4 text-sm text-slate-600 pb-4 border-b border-slate-200">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(viewingNote.created_at).toLocaleString()}</span>
              </div>
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>{viewingNote.created_by_user?.full_name || 'Unknown'}</span>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium bg-${noteTypes.find(t => t.value === viewingNote.note_type)?.color || 'blue'}-100 text-${noteTypes.find(t => t.value === viewingNote.note_type)?.color || 'blue'}-700`}>
                {noteTypes.find(t => t.value === viewingNote.note_type)?.label || 'Note'}
              </span>
              {viewingNote.is_private && (
                <span className="px-2 py-1 rounded text-xs font-medium bg-slate-200 text-slate-600">
                  Private
                </span>
              )}
            </div>

            {/* Note Content */}
            <div className="prose max-w-none">
              <p className="text-slate-700 whitespace-pre-wrap">{viewingNote.content}</p>
            </div>

            {/* Attachments */}
            {viewingNote.attachments && viewingNote.attachments.length > 0 && (
              <div className="pt-4 border-t border-slate-200">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center space-x-2">
                  <Paperclip className="w-4 h-4" />
                  <span>Attachments ({viewingNote.attachments.length})</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {viewingNote.attachments.map((attachment) => {
                    const FileIcon = getFileIcon(attachment.file_type);
                    const isImage = attachment.file_type?.startsWith('image/');
                    
                    return (
                      <div key={attachment.id} className="border border-slate-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                        {isImage && attachment.file_url && (
                          <img 
                            src={attachment.file_url} 
                            alt={attachment.file_name}
                            className="w-full h-32 object-cover rounded mb-2"
                          />
                        )}
                        <div className="flex items-center space-x-2">
                          <FileIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {attachment.file_name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {(attachment.file_size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <div className="flex space-x-1">
                            {attachment.file_url && (
                              <a
                                href={attachment.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              onClick={() => handleDownloadAttachment(attachment)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <button
                onClick={() => {
                  setEditingNote(viewingNote);
                  setNoteForm({
                    title: viewingNote.title,
                    content: viewingNote.content,
                    noteType: viewingNote.note_type,
                    isPrivate: viewingNote.is_private
                  });
                  setViewingNote(null);
                }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors flex items-center space-x-2"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={() => setViewingNote(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900">
            {editingNote ? 'Edit Note' : 'Add New Note'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mx-6 mt-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-6 text-center text-slate-500">Loading notes...</div>
        ) : (
          <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
            {notes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No notes yet. Create your first one below.
              </div>
            ) : (
              notes.map(note => (
                <div 
                  key={note.id} 
                  className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleViewNote(note)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium text-slate-900">{note.title}</h4>
                        {note.attachments && note.attachments.length > 0 && (
                          <span className="flex items-center space-x-1 text-xs text-slate-500">
                            <Paperclip className="w-3 h-3" />
                            <span>{note.attachments.length}</span>
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{note.content}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-slate-500">
                        <span>{new Date(note.created_at).toLocaleDateString()}</span>
                        <span>by {note.created_by_user?.full_name || 'Unknown'}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingNote(note);
                        setNoteForm({
                          title: note.title,
                          content: note.content,
                          noteType: note.note_type,
                          isPrivate: note.is_private
                        });
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 border-t border-slate-200 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Note Type
            </label>
            <select
              value={noteForm.noteType}
              onChange={(e) => setNoteForm(prev => ({ ...prev, noteType: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {noteTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={noteForm.title}
              onChange={(e) => setNoteForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Q4 Planning Call"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Content *
            </label>
            <textarea
              value={noteForm.content}
              onChange={(e) => setNoteForm(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Enter note details..."
              rows={8}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              required
            />
          </div>

          {!editingNote && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Attachments
              </label>
              <div className="space-y-2">
                <div
                  ref={dropZoneRef}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full px-4 py-8 border-2 border-dashed rounded-lg transition-all cursor-pointer ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-300 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm text-slate-600 mb-1">
                      <span className="font-medium text-blue-600">Click to browse</span> or drag and drop
                    </p>
                    <p className="text-xs text-slate-500">
                      Images, PDFs, documents up to 10MB
                    </p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {selectedFiles.length > 0 && (
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => {
                      const FileIcon = getFileIcon(file.type);
                      return (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-white border border-slate-200 rounded-lg">
                          <FileIcon className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-700 flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-slate-500">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSelectedFile(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={noteForm.isPrivate}
                onChange={(e) => setNoteForm(prev => ({ ...prev, isPrivate: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Private note (visible only to admins)</span>
            </label>
          </div>

          <div className="flex space-x-2 pt-4">
            {editingNote && (
              <button
                type="button"
                onClick={() => {
                  setEditingNote(null);
                  setNoteForm({
                    title: '',
                    content: '',
                    noteType: 'general',
                    isPrivate: false
                  });
                }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{editingNote ? 'Update' : 'Save'} Note</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientNotes;